import {
  CREATE_ADDRESS_RESPONSE,
  DESTROY_ADDRESS_RESPONSE,
  UPDATE_ADDRESS_RESPONSE,
  SET_DEFAULT_ADDRESS_RESPONSE,
} from 'actions/addresses';
import {
  ADD_BASKET_RESPONSE,
  UNDONATE_BASKET_RESPONSE,
  DONATE_BASKET_RESPONSE,
  SKIP_BASKET_RESPONSE,
  UPDATE_BASKET_ADDRESS_RESPONSE,
} from 'actions/baskets';
import { BOOTSTRAP_COOKBOOK } from 'actions/cookbook';
import { ADD_FAVORITE_RESPONSE, REMOVE_FAVORITE_RESPONSE } from 'actions/favorites';
import {
  UPDATE_MEAL_FILTER,
  UPDATE_MEAL_FILTER_V3,
  DASHBOARD_CLEAR_ALL_FILTERS,
  CLEAR_MEAL_FILTER,
} from 'actions/mealFilters';
import { GET_MEALS_REQUEST } from 'actions/meals';
import { BOOTSTRAP_MESSAGE_CENTER, MESSAGE_INTERACTION } from 'actions/messageCenter';
import { BOOTSTRAP_DASHBOARD } from 'actions/dashboard';
import { ADD_REVIEW_FOR_COOKBOOK_RESPONSE } from 'actions/reviews';
import { SET_FAVORITE } from 'actions/searchParams';
import { UPDATE_TASTE_PROFILE_RESPONSE } from 'actions/tasteProfile';
import {
  UPDATE_USER_RESPONSE,
  GET_USER_RESPONSE,
  PAUSE_USER_REQUEST,
  UPDATE_PAYMENT_RESPONSE,
  UPDATE_PASSWORD_RESPONSE,
  UPDATE_MEAL_ADDON_SELECTIONS_RESPONSE,
  UPDATE_OPT_IN_SELECTIONS_RESPONSE,
  UPDATE_REACTIVATION_RESPONSE,
} from 'actions/user';
import * as Helpers from 'helpers/analytics';
import { triggerUsabillaCampaign } from 'helpers/triggerUsabillaCampaign';
import { TEMPO } from 'lib/constants';
import {
  ADD_MEAL_V3,
  BOOTSTRAP_DASHBOARD_V3,
  GET_USER_V3_RESPONSE,
  REMOVE_MEAL_V3,
  UPDATE_BASKET_V3_RESPONSE,
} from 'lib/constants/v3Actions';
import mealsSlice from 'reducers/cookbook/meals';
import { selectRestartCampaign } from 'reducers/restartCampaign';
import { makeSelectRelativeMenuWeek, selectBasket, selectMealForMenu } from 'selectors/dashboard';
import * as Amplitude from 'services/amplitude';

const selectRelativeMenuWeek = makeSelectRelativeMenuWeek();

const handleLogging = (store, action) => {
  switch (action.type) {
    case BOOTSTRAP_MESSAGE_CENTER:
    case BOOTSTRAP_COOKBOOK:
    case BOOTSTRAP_DASHBOARD:
    case BOOTSTRAP_DASHBOARD_V3:
      if (!Amplitude.isInitialized()) Amplitude.init();
      break;

    case MESSAGE_INTERACTION: {
      const amplitudeEventName = action.message.amplitudeEventName;
      if (amplitudeEventName) Amplitude.track(action);
      break;
    }

    case GET_USER_RESPONSE:
    case GET_USER_V3_RESPONSE:
      Amplitude.setAmplitudeUserProperties(action.response.user);
      break;

    case UPDATE_REACTIVATION_RESPONSE: {
      const state = store.getState();
      const campaign = selectRestartCampaign(state);

      triggerUsabillaCampaign('reactivation', {
        UUID: state.user.uuid,
        numberOfBoxes: action.response.numberOfBoxes,
        mealPlan: state.user.mealPlanSlug,
      });

      Amplitude.track(action, Helpers.buildReactivateCampaignEvent(campaign));
      break;
    }

    case UPDATE_USER_RESPONSE:
    case UPDATE_TASTE_PROFILE_RESPONSE:
    case PAUSE_USER_REQUEST:
    case UPDATE_PAYMENT_RESPONSE:
    case UPDATE_PASSWORD_RESPONSE:
    case UPDATE_MEAL_ADDON_SELECTIONS_RESPONSE:
    case UPDATE_OPT_IN_SELECTIONS_RESPONSE:
    case CREATE_ADDRESS_RESPONSE:
    case DESTROY_ADDRESS_RESPONSE:
    case UPDATE_ADDRESS_RESPONSE:
    case SET_DEFAULT_ADDRESS_RESPONSE:
    case UPDATE_BASKET_ADDRESS_RESPONSE:
      Amplitude.track(action);
      break;

    case GET_MEALS_REQUEST: {
      const data = Helpers.buildSearchEvent(action);
      if (!data) break;

      Amplitude.track(action, data);
      break;
    }

    case SKIP_BASKET_RESPONSE: {
      const state = store.getState();
      const triggerName =
        state.user.mealPlanSlug === TEMPO ? 'skip feedback tempo' : 'skip feedback';

      triggerUsabillaCampaign(triggerName, {
        skippedMenu: action.params.menuSlug,
        UUID: state.user.uuid,
        numberOfBoxes: action.response.numberOfBoxes,
        mealPlan: state.user.mealPlanSlug,
      });

      break;
    }

    case DONATE_BASKET_RESPONSE: {
      const state = store.getState();
      const basket = selectBasket(state, action.params.menuSlug);
      const relativeMenuWeek = selectRelativeMenuWeek(state, basket.menu.slug);

      Amplitude.track(action, Helpers.buildBasketEvent(basket, relativeMenuWeek));
      break;
    }

    case UNDONATE_BASKET_RESPONSE:
    case ADD_BASKET_RESPONSE: {
      const state = store.getState();
      const basket = action.response.weeklyBasket;
      const relativeMenuWeek = selectRelativeMenuWeek(state, basket.menu.slug);

      Amplitude.track(action, Helpers.buildBasketEvent(basket, relativeMenuWeek));
      break;
    }

    case UPDATE_BASKET_V3_RESPONSE: {
      const state = store.getState();
      const basket = action.response.weeklyBasket;
      const editStage = action.params.editStage;
      const relativeMenuWeek = selectRelativeMenuWeek(state, basket.menu.slug);

      Amplitude.track(action, Helpers.buildBasketV3Event(basket, editStage, relativeMenuWeek));

      break;
    }

    case REMOVE_MEAL_V3:
    case ADD_MEAL_V3: {
      const state = store.getState();
      const meal = selectMealForMenu(state, action.menuSlug, action.mealId);
      const editStage = action.editStage;
      const relativeMenuWeek = selectRelativeMenuWeek(state, action.menuSlug);

      if (meal && editStage) {
        Amplitude.track(action, Helpers.buildV3MealEvent(meal, editStage, relativeMenuWeek));
      }

      break;
    }

    case ADD_REVIEW_FOR_COOKBOOK_RESPONSE: {
      const { selectMeal } = mealsSlice.selectors;
      const meal = selectMeal(store.getState(), action.response.mealSelectionId);

      Amplitude.track(action, Helpers.buildReviewEvent(action.response.recipeRating, meal));

      break;
    }

    case ADD_FAVORITE_RESPONSE:
    case REMOVE_FAVORITE_RESPONSE:
      Amplitude.track(action);
      break;

    case DASHBOARD_CLEAR_ALL_FILTERS:
      if (action.shouldLog) {
        Amplitude.track(action, action.props);
      }
      break;

    case CLEAR_MEAL_FILTER:
      Amplitude.track(action);
      break;

    case UPDATE_MEAL_FILTER:
    case UPDATE_MEAL_FILTER_V3:
      if (!action.isQuickFilter) {
        const event = Helpers.buildSlideoutFilterEvent(action);

        if (event) {
          Amplitude.track(action, event);
        }
      }
      break;

    case SET_FAVORITE:
      Amplitude.track(action);
      break;
  }
};

const middleware = store => next => action => {
  try {
    handleLogging(store, action);
  } catch {
    // no-op
  }

  return next(action);
};

export default middleware;
